import { CONVERSATION_TITLE_MAX, type ConversationDetail, CSRF_HEADER } from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ArchiveRestore,
  Ellipsis,
  FolderClosed,
  FolderInput,
  ListChecks,
  MessageCircle,
  Pencil,
  Plug,
  Share2,
  Trash2,
} from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { api } from "../api.ts";
import { apiError } from "../api-errors.ts";
import { Link, projectHomePath, projectTaskPath } from "../router.tsx";
import { useSession } from "../session.tsx";
import { AudienceLine } from "../trust/AudienceLine.tsx";
import { DeleteConversationDialog, useArchive } from "./actions.tsx";
import { headerFocusPending, MoveDialog, takeHeaderFocus } from "./MoveDialog.tsx";
import { conversationKeys } from "./queries.ts";

/**
 * The header of every conversation (spec `web-shell` "Conversation header"): its title (inline
 * rename for the creator), its project or "No project", the audience line, and the actions the
 * viewer may take: share, "Move to project" (a standalone conversation's creator), then Rename,
 * Archive and Delete in a menu. A recipient sees who shared it and no action.
 */
export function ConversationHeader(props: {
  conversation: ConversationDetail;
  /** The caller created it. */
  owned: boolean;
  /** May open the share dialog (creator, capability; read-only on a frozen project). */
  canShare: boolean;
  /** May rename, archive or delete now (creator; a frozen project takes no rename). */
  canRename: boolean;
  audience: { text: string; restricted: boolean } | null;
  webResults?: boolean;
  onShare: () => void;
  /** Shown before the actions (the knowledge panel's toggle in a project). */
  extra?: ReactNode;
}) {
  const { conversation, owned } = props;
  const { t } = useTranslation();
  const headingId = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  const [renaming, setRenaming] = useState(false);
  const [moving, setMoving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const menu = useRef<HTMLButtonElement>(null);
  // Rename picked from the menu: the field takes focus instead of the menu's trigger.
  const toRename = useRef(false);
  const archive = useArchive(conversation.id);
  const standalone = conversation.projectId === null;
  const title = conversation.title ?? t("conversation.untitled");

  useEffect(() => {
    // Opened by a move: focus the header, so the new place is announced.
    if (headerFocusPending(conversation.id)) {
      // After the closed dialog has handed focus back (its opener is gone by then); taken only
      // when it happens, so a replayed effect (StrictMode) still focuses.
      const timer = setTimeout(() => {
        if (takeHeaderFocus(conversation.id)) {
          heading.current?.focus();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [conversation.id]);

  return (
    <header
      data-esi-content="conversations"
      className="flex h-bar shrink-0 items-center justify-between gap-3 border-b border-line px-3 py-1.5 sm:px-4"
    >
      <div className="flex min-w-0 flex-1 flex-col">
        {renaming ? (
          <RenameField
            id={conversation.id}
            current={conversation.title ?? ""}
            labelId={headingId}
            onDone={() => {
              setRenaming(false);
              requestAnimationFrame(() => heading.current?.focus());
            }}
          />
        ) : (
          // The page's one heading, standalone or in a project (named once, just below).
          <h1
            id={headingId}
            ref={heading}
            tabIndex={-1}
            className="truncate text-sm font-semibold text-fg outline-none"
            title={title}
          >
            {title}
          </h1>
        )}
        {/* One line on the 48px bar: the project name gives way first, nothing wraps under it. */}
        <div className="flex min-w-0 items-center gap-x-2 overflow-hidden sm:gap-x-3 text-xs whitespace-nowrap text-fg-muted">
          {conversation.project ? (
            <ProjectLink project={conversation.project} />
          ) : standalone ? (
            <span className="inline-flex items-center gap-1">
              <MessageCircle aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
              {t("conversationHeader.noProject")}
            </span>
          ) : null}
          {conversation.task && conversation.projectId ? (
            // The task it works on (tasks-conversations), from this response only: a link with its
            // title while the viewer can read the task, else an unavailable task with neither.
            conversation.task.title !== null ? (
              <Link
                href={projectTaskPath(conversation.projectId, conversation.task.id)}
                aria-label={t("conversationHeader.task", { title: conversation.task.title })}
                title={conversation.task.title}
                className="inline-flex min-w-0 max-w-[60%] items-center gap-1 underline-offset-4 hover:text-fg hover:underline"
              >
                <ListChecks aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
                <span className="truncate">{conversation.task.title}</span>
              </Link>
            ) : (
              <span
                className="inline-flex shrink-0 items-center gap-1"
                title={t("conversationHeader.taskUnavailableHint")}
              >
                <ListChecks aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
                {t("conversationHeader.taskUnavailable")}
              </span>
            )
          ) : null}
          {conversation.client ? (
            // Who asked through which client (spec `trust-indicators`): shown to every viewer.
            <span
              className="inline-flex max-w-[70%] shrink-0 items-center gap-1"
              title={t("conversationHeader.viaClientHint", { id: conversation.client.id })}
            >
              <Plug aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
              <span className="min-w-0 truncate">
                {conversation.client.host
                  ? t("conversationHeader.viaClient", { host: conversation.client.host })
                  : t("conversationHeader.viaCli")}
              </span>
            </span>
          ) : null}
          {props.audience ? (
            <AudienceLine
              className="shrink-0"
              restricted={props.audience.restricted}
              text={props.audience.text}
              webResults={props.webResults}
            />
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-0.5">
        {props.extra}
        {owned && props.canShare ? (
          <HeaderAction label={t("conversation.share")} onClick={props.onShare}>
            <Share2 aria-hidden="true" strokeWidth={1.75} />
          </HeaderAction>
        ) : null}
        {owned && standalone ? (
          <HeaderAction label={t("conversationHeader.move")} onClick={() => setMoving(true)}>
            <FolderInput aria-hidden="true" strokeWidth={1.75} />
          </HeaderAction>
        ) : null}
        {owned ? (
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    ref={menu}
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t("conversationHeader.more")}
                    className="text-fg-muted hover:text-fg"
                  >
                    <Ellipsis aria-hidden="true" strokeWidth={1.75} />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>{t("conversationHeader.more")}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent
              align="end"
              className="min-w-44"
              onCloseAutoFocus={(event) => {
                // The field opens once the menu has closed (its focus trap is gone).
                if (toRename.current) {
                  toRename.current = false;
                  event.preventDefault();
                  setRenaming(true);
                }
              }}
            >
              {props.canRename ? (
                <DropdownMenuItem
                  onSelect={() => {
                    toRename.current = true;
                  }}
                >
                  <Pencil aria-hidden="true" strokeWidth={1.75} />
                  {t("conversationHeader.rename")}
                </DropdownMenuItem>
              ) : null}
              {conversation.archivedAt ? (
                <DropdownMenuItem onSelect={() => archive.mutate(false)}>
                  <ArchiveRestore aria-hidden="true" strokeWidth={1.75} />
                  {t("conversationActions.unarchive")}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onSelect={() => archive.mutate(true)}>
                  <Archive aria-hidden="true" strokeWidth={1.75} />
                  {t("conversationActions.archive")}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={() => setDeleting(true)}>
                <Trash2 aria-hidden="true" strokeWidth={1.75} />
                {t("conversationActions.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
      {standalone && owned ? (
        <MoveDialog
          open={moving}
          onClose={() => setMoving(false)}
          conversationId={conversation.id}
        />
      ) : null}
      <DeleteConversationDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        conversation={conversation}
        returnFocus={menu.current}
      />
    </header>
  );
}

/**
 * The screen's one mention of a project (spec `web-shell` "Project workspace"): a muted folder
 * and its name, linking to the project home; it truncates first, phones included.
 */
export function ProjectLink({ project }: { project: { id: string; name: string } }) {
  return (
    <Link
      href={projectHomePath(project.id)}
      className="inline-flex min-w-0 items-center gap-1 underline-offset-4 hover:text-fg hover:underline"
    >
      <FolderClosed aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
      <span className="truncate">{project.name}</span>
    </Link>
  );
}

/** A quiet icon-only action: its name is its tooltip and accessible label. */
function HeaderAction(props: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={props.label}
          onClick={props.onClick}
          className="text-fg-muted hover:text-fg"
        >
          {props.children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{props.label}</TooltipContent>
    </Tooltip>
  );
}

/** The title as an input of the same metrics: Enter saves, Escape keeps the old title. */
function RenameField(props: { id: string; current: string; labelId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const [value, setValue] = useState(props.current);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    input.current?.focus();
    input.current?.select();
  }, []);
  const rename = useMutation({
    mutationFn: async (title: string) => {
      const res = await api.api.conversations[":conversationId"].$patch(
        { param: { conversationId: props.id }, json: { title } },
        { headers: { [CSRF_HEADER]: csrfToken } },
      );
      if (!res.ok) {
        throw await apiError(res);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.lists });
      void queryClient.invalidateQueries({ queryKey: conversationKeys.detail(props.id) });
      props.onDone();
    },
  });
  const trimmed = value.trim();
  return (
    <form
      className="flex flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        if (trimmed === "" || trimmed === props.current) {
          props.onDone();
        } else if (!rename.isPending) {
          rename.mutate(trimmed);
        }
      }}
    >
      <input
        ref={input}
        value={value}
        maxLength={CONVERSATION_TITLE_MAX}
        aria-label={t("conversationHeader.renameLabel")}
        aria-invalid={rename.isError || undefined}
        disabled={rename.isPending}
        onChange={(e) => setValue(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            props.onDone();
          }
        }}
        onBlur={() => {
          if (!rename.isPending && !rename.isError) {
            props.onDone();
          }
        }}
        className="-mx-1 h-5 rounded-sm bg-transparent px-1 text-sm font-semibold text-fg outline-none ring-ring focus-visible:ring-2"
      />
      {rename.isError ? (
        <span role="alert" className="text-xs text-danger-fg">
          {rename.error.message}
        </span>
      ) : null}
    </form>
  );
}
